import {Button} from '@/components/ui/button/Button'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {useStoreProjectExportSiar} from '@/features/projects/queries/useStoreProjectExportSiar'
import {ExportSiarType} from '@/features/projects/types'
import {useTranslation} from '@/translations/i18n'
import {buildFile, downloadFile} from '@/utilities/helpers'
import {toast} from '@/utilities/toast/toast'
import dayjs from 'dayjs'
import {ComponentType, MouseEvent, useCallback} from 'react'

export const ExportSiarTrigger = ({
    isDisabled,
    customTrigger: CustomTrigger,
    onExportSuccess,
    exportSiarType,
    projectIds,
    addRegistry
}: {
    isDisabled?: boolean
    customTrigger?: ComponentType<{disabled: boolean; onClick: (event?: MouseEvent<HTMLElement>) => void}>
    onExportSuccess?: () => void
    exportSiarType: ExportSiarType | null
    projectIds: string[]
    addRegistry: boolean
}) => {
    const {t} = useTranslation()

    const {mutate: storeProjectExportSiar, isPending} = useStoreProjectExportSiar({
        options: {
            onSuccess: data => {
                const zipFile = buildFile({extension: 'zip', fileName: `siar-${dayjs().format('DD-MM-YYYY')}`, data})
                downloadFile(zipFile)
                onExportSuccess?.()
            },
            onError: error => {
                toast.error(error.message)
            }
        }
    })

    const handleExportSiar = useCallback(() => {
        if (!exportSiarType) {
            toast.error(t('projects:export_siar_error'))
            return
        }

        if (projectIds.length === 0) {
            toast.error(t('projects:export_siar_error_projects'))
            return
        }

        storeProjectExportSiar({
            projectIds,
            addRegistry,
            exportType: exportSiarType
        })
    }, [projectIds, addRegistry, exportSiarType, storeProjectExportSiar])

    return CustomTrigger ? (
        <CustomTrigger disabled={!!isDisabled || isPending} onClick={handleExportSiar} />
    ) : (
        <Button disabled={!!isDisabled || isPending} onClick={handleExportSiar} variant="primaryNeutral">
            {t('commons:export')}
            {isPending && <Spinner />}
        </Button>
    )
}
